import { useState, useEffect, useCallback, useRef, useContext } from "react";
import { post, C, LOGO_BASE, THUMB_BASE } from "./utils";
import { ThemeCtx, Icon } from "./AppShell";
import ReactDOM from "react-dom";

const openDocTab = (doc, subId) => {
  const base = window.location.origin + window.location.pathname;
  window.open(`${base}?doc=${doc.DOCUMENTID}&dt=${doc.DOCTYPEID||96}&sub=${subId||""}`, "_blank", "noopener,noreferrer");
};

const CLINICAL_TRIALS_TYPES = "96";

const isImage   = (p="") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);
const fileName  = (p="") => p.split("/").pop();
const stripHtml = (h="") => h ? h.replace(/<[^>]*>/g," ").replace(/\s+/g," ").trim() : "";
const getPrimaryProduct    = (a=[]) => a.find(p=>(p.PRIMARY==="1"||p.PRIMARY===1)&&String(p.BRANDID)!=="0")?.BRANDNAME||"";
const getPrimaryIndication = (a=[]) => a.find(i=>(i.PRIMARY==="1"||i.PRIMARY===1)&&String(i.INDICATIONID)!=="0")?.INDICATIONNAME||"";
const getCompanyName = c => c?.SPONSORNAME || "";
const getCompanyLogo = c => c?.COMPANY_LOGO ? LOGO_BASE+c.COMPANY_LOGO : null;

const PHASE_CFG = {
  "Approved":  { bar:"#059669", light:"#ECFDF5", text:"#065F46", grad:"linear-gradient(90deg,#059669,#34D399)" },
  "Phase III": { bar:"#1D4ED8", light:"#EFF6FF", text:"#1E3A8A", grad:"linear-gradient(90deg,#1D4ED8,#60A5FA)" },
  "Phase II":  { bar:"#7C3AED", light:"#F5F3FF", text:"#4C1D95", grad:"linear-gradient(90deg,#7C3AED,#C084FC)" },
  "Phase I":   { bar:"#0891B2", light:"#ECFEFF", text:"#164E63", grad:"linear-gradient(90deg,#0891B2,#67E8F9)" },
  "Other":     { bar:"#6B7280", light:"#F9FAFB", text:"#374151", grad:"linear-gradient(90deg,#6B7280,#D1D5DB)" },
};
const PHASE_ORDER = ["Approved","Phase III","Phase II","Phase I","Other"];

// ─── Country flag (SVG inline) ────────────────
function FlagIcon({ country, size=15 }) {
  const c = (country||"").trim();
  if (c==="US"||c==="USA"||c==="United States") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{borderRadius:"50%",flexShrink:0}}>
      <clipPath id="ct_us"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#ct_us)">
        <rect width="32" height="32" fill="#B22234"/>
        {[0,1,2,3,4,5].map(i=><rect key={i} y={i*4.9+2.45} width="32" height="2.45" fill="#fff"/>)}
        <rect width="13" height="15" fill="#3C3B6E"/>
        {[[2,2],[5,2],[8,2],[11,2],[3.5,4.5],[6.5,4.5],[9.5,4.5],[2,7],[5,7],[8,7],[11,7],[3.5,9.5],[6.5,9.5],[9.5,9.5],[2,12],[5,12],[8,12],[11,12]].map(([x,y],i)=>(
          <circle key={i} cx={x} cy={y} r="0.9" fill="#fff"/>
        ))}
      </g>
    </svg>
  );
  if (c==="EU"||c==="Europe"||c==="European Union") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{borderRadius:"50%",flexShrink:0}}>
      <clipPath id="ct_eu"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#ct_eu)">
        <rect width="32" height="32" fill="#003399"/>
        {Array.from({length:12},(_,i)=>{
          const a=(i/12)*2*Math.PI-Math.PI/2;
          return <text key={i} x={16+10*Math.cos(a)-3} y={16+10*Math.sin(a)+4} fontSize="6" fill="#FFDD00">★</text>;
        })}
      </g>
    </svg>
  );
  if (c==="Global"||c==="International"||c==="Worldwide") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{borderRadius:"50%",flexShrink:0}}>
      <circle cx="16" cy="16" r="16" fill="#E0F2FE"/>
      <circle cx="16" cy="16" r="16" fill="none" stroke="#0891B2" strokeWidth="1"/>
      <ellipse cx="16" cy="16" rx="7" ry="16" fill="none" stroke="#0891B2" strokeWidth="1"/>
      <line x1="0" y1="16" x2="32" y2="16" stroke="#0891B2" strokeWidth="1"/>
      <line x1="16" y1="0" x2="16" y2="32" stroke="#0891B2" strokeWidth="1"/>
    </svg>
  );
  if (c==="UK"||c==="United Kingdom") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{borderRadius:"50%",flexShrink:0}}>
      <clipPath id="ct_uk"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#ct_uk)">
        <rect width="32" height="32" fill="#012169"/>
        <path d="M0,0 L32,32 M32,0 L0,32" stroke="#fff" strokeWidth="6"/>
        <path d="M0,0 L32,32 M32,0 L0,32" stroke="#C8102E" strokeWidth="3"/>
        <path d="M16,0 L16,32 M0,16 L32,16" stroke="#fff" strokeWidth="8"/>
        <path d="M16,0 L16,32 M0,16 L32,16" stroke="#C8102E" strokeWidth="5"/>
      </g>
    </svg>
  );
  if (c==="Japan"||c==="JP") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{borderRadius:"50%",flexShrink:0}}>
      <clipPath id="ct_jp"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#ct_jp)">
        <rect width="32" height="32" fill="#fff"/>
        <circle cx="16" cy="16" r="8" fill="#BC002D"/>
      </g>
    </svg>
  );
  if (c==="Canada"||c==="CA") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{borderRadius:"50%",flexShrink:0}}>
      <clipPath id="ct_ca"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#ct_ca)">
        <rect width="32" height="32" fill="#fff"/>
        <rect width="8" height="32" fill="#FF0000"/>
        <rect x="24" width="8" height="32" fill="#FF0000"/>
      </g>
    </svg>
  );
  if (!c) return null;
  const abbr = c.slice(0,2).toUpperCase();
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{borderRadius:"50%",flexShrink:0}}>
      <circle cx="16" cy="16" r="16" fill="#64748B"/>
      <text x="16" y="21" textAnchor="middle" fontSize="12" fontWeight="700" fill="#fff">{abbr}</text>
    </svg>
  );
}


// Map any phase string to a canonical phase key
function normalizePhase(raw) {
  if (!raw) return null;
  const t = raw.toString().toUpperCase().trim();
  if (t.includes("APPROV"))                           return "Approved";
  if (t.includes("III")||t.includes("3"))             return "Phase III";
  // Check II before I to avoid false match
  if (t.includes("II")||t.includes("2"))              return "Phase II";
  if (t.includes("I")||t.includes("1"))               return "Phase I";
  return null;
}

function detectPhase(doc) {
  // 1. Check exact "PHASE" key from API response first
  if (doc.PHASE) {
    const norm = normalizePhase(doc.PHASE);
    if (norm) return norm;
  }

  // 2. Fall back to scanning subject/notes/acronyms text
  const t = `${doc.SUBJECT||""} ${doc.NOTES||""} ${doc.ACRONYMS||""}`.toUpperCase();
  if (t.includes("APPROV"))                            return "Approved";
  if (t.includes("PHASE III")||t.includes("PHASE 3")) return "Phase III";
  if (t.includes("PHASE II")||t.includes("PHASE 2"))  return "Phase II";
  if (t.includes("PHASE I")||t.includes("PHASE 1"))   return "Phase I";
  return "Other";
}

// Raw phase text for tooltip when phase is "Other"
function getRawPhase(doc) {
  return doc.PHASE || "";
}

function parseDate(raw) {
  if (!raw) return null;
  const MON={Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11};
  let m;
  m=raw.match(/(\d{1,2})\s+([A-Za-z]+)[,\s]+(\d{4})/); if(m) return new Date(+m[3],MON[m[2]]??0,+m[1]);
  m=raw.match(/(\d{4})-(\d{2})-(\d{2})/);               if(m) return new Date(+m[1],+m[2]-1,+m[3]);
  m=raw.match(/([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})/);  if(m) return new Date(+m[3],MON[m[1]]??0,+m[2]);
  m=raw.match(/^(\d{4})$/);                              if(m) return new Date(+m[1],0,1);
  return null;
}
const fmtDate = d => d ? d.toLocaleDateString("en-GB",{month:"short",year:"numeric"}) : "";

// ─── Data loader ──────────────────────────────
function useClinicalLoader({ user, subproject }) {
  const [docs,setDocs]=useState([]), [loading,setLoading]=useState(false), [error,setError]=useState(null);
  const load = useCallback(async () => {
    if (!subproject) return;
    setLoading(true); setError(null);
    try {
      const r = await post("advance_filter",{
        subprojectid:subproject.SUBPROJECTID, userid:user.USERID,
        doctypeid:CLINICAL_TRIALS_TYPES, stateid:2, sort:"latest",
      }, user.AUTH_CODE);
      setDocs(r.STATUS==="1"&&Array.isArray(r.DATA)?r.DATA:[]);
    } catch { setError("Failed to load clinical trials."); }
    setLoading(false);
  }, [subproject, user]);
  useEffect(()=>{ load(); },[subproject?.SUBPROJECTID]);
  return { docs, loading, error };
}

function buildEntries(docs) {
  return docs.map(doc => {
    const companies   = Array.isArray(doc.COMPANY) ? doc.COMPANY : [];
    // Prefer PRIMARY company for logo, fallback to first
    const company     = companies.find(c => c.PRIMARY===1||c.PRIMARY==="1") || companies[0] || null;
    const products    = Array.isArray(doc.PRODUCTS)    ? doc.PRODUCTS    : [];
    const indications = Array.isArray(doc.INDICATIONS) ? doc.INDICATIONS : [];
    const chatbot     = Array.isArray(doc.CHATBOT)     ? doc.CHATBOT     : [];
    const imgFiles    = chatbot.filter(a=>isImage(a.FILEPATH));
    const countries = [...new Set([
      doc.COUNTRYNAME||"",
      ...companies.map(c => c.COUNTRYNAME||"")
    ].filter(Boolean))];
    return {
      id:doc.DOCUMENTID, subject:doc.SUBJECT||"Untitled",
      notes:stripHtml(doc.NOTES||""),
      acronym:typeof doc.ACRONYMS==="string"?doc.ACRONYMS:"",
      phase:detectPhase(doc),
      rawPhase:getRawPhase(doc),
      // Primary sponsor name — from doc root or PRIMARY company or first company
      sponsor: doc.SPONSORNAME ||
               getCompanyName(company) || "",
      // All sponsor names for filter
      allSponsors: [...new Set(
        [doc.SPONSORNAME, ...companies.map(c => c.SPONSORNAME)].filter(Boolean)
      )],
      logoUrl: getCompanyLogo(company),
      product:getPrimaryProduct(products), indication:getPrimaryIndication(indications),
      thumbUrl:imgFiles.length>0?THUMB_BASE+fileName(imgFiles[0].FILEPATH):null,
      startDate:parseDate(doc.STARTDATE||doc.START_DATE||doc.STIME||doc.DISPLAY_TIME),
      endDate:parseDate(doc.ENDDATE||doc.END_DATE||doc.ETIME),
      displayTime:doc.DISPLAY_TIME||"",
      countries,
      count:doc.COUNT||0, likes:doc.LIKES||0, comments:doc.COMMENTCOUNT||0,
      fav:doc.MYFAVOURITES==="YES",
    };
  });
}

// ─── Smart tooltip position ──────────────────
function smartPos(x, y, tipW=340, tipH=220) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = x + 18;
  let top  = y - 14;
  if (left + tipW > vw - 8)  left = x - tipW - 10;
  if (left < 8)               left = 8;
  if (top + tipH > vh - 8)   top  = vh - tipH - 8;
  if (top < 8)                top  = 8;
  return { left, top };
}
// ─── Tooltip ──────────────────────────────────
function Tooltip({ entry, x, y }) {
  const ph = PHASE_CFG[entry.phase]||PHASE_CFG["Other"];
  const dur = entry.startDate&&entry.endDate
    ? Math.round((entry.endDate-entry.startDate)/(1000*60*60*24*30)) : null;
  return ReactDOM.createPortal(
    <div style={{position:"fixed", ...smartPos(x, y),zIndex:99999,pointerEvents:"none",
      background:"#0F172A",color:"#fff",borderRadius:"14px",padding:"14px 16px",
      minWidth:"270px",maxWidth:"360px",fontSize:"12px",lineHeight:"1.6",
      boxShadow:`0 20px 60px rgba(0,0,0,0.5),0 0 0 1px ${ph.bar}55`}}>
      <div style={{height:"3px",background:ph.grad,borderRadius:"2px",marginBottom:"10px"}}/>
      <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"8px"}}>
        {entry.logoUrl
          ? <img src={entry.logoUrl} alt="" onError={e=>e.target.style.display="none"}
              style={{width:"28px",height:"28px",objectFit:"contain",borderRadius:"6px",background:"#fff",padding:"2px"}}/>
          : <div style={{width:"28px",height:"28px",borderRadius:"6px",background:ph.bar,
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <span style={{fontSize:"13px",fontWeight:"800",color:"#fff"}}>{entry.sponsor?.[0]||"?"}</span>
            </div>}
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:"800",fontSize:"14px",color:"#F1F5F9",lineHeight:1.2}}>
            {entry.product||entry.subject.split(" ").slice(0,4).join(" ")}
          </div>
          {entry.sponsor&&<div style={{fontSize:"11px",color:"#64748B",marginTop:"1px"}}>{entry.sponsor}</div>}
        </div>
        <span style={{fontSize:"10px",fontWeight:"700",color:ph.bar,background:`${ph.bar}22`,
          borderRadius:"4px",padding:"2px 8px",border:`1px solid ${ph.bar}44`,flexShrink:0}}>
          {entry.phase==="Other"&&entry.rawPhase ? entry.rawPhase : entry.phase}
        </span>
      </div>
      {entry.countries&&entry.countries.length>0&&(
        <div style={{display:"flex",alignItems:"center",gap:"5px",marginBottom:"5px",flexWrap:"wrap"}}>
          {entry.countries.map((c,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:"4px"}}>
              <FlagIcon country={c} size={14}/>
              <span style={{fontSize:"10px",color:"#94A3B8"}}>{c}</span>
            </div>
          ))}
        </div>
      )}
      {entry.acronym&&<div style={{color:"#FCD34D",fontWeight:"800",fontSize:"13px",marginBottom:"3px"}}>{entry.acronym}</div>}
      {entry.indication&&<div style={{color:"#A5B4FC",fontWeight:"600",marginBottom:"5px"}}>{entry.indication}</div>}
      {entry.subject&&<div style={{color:"#94A3B8",fontSize:"11px",lineHeight:"1.5",marginBottom:"8px",
        borderLeft:`2px solid ${ph.bar}55`,paddingLeft:"8px"}}>
        {entry.subject.length>120?entry.subject.slice(0,120)+"…":entry.subject}</div>}
      <div style={{display:"flex",alignItems:"center",gap:"10px",paddingTop:"8px",borderTop:"1px solid rgba(255,255,255,0.08)"}}>
        {entry.startDate&&<span style={{color:"#6EE7B7",fontWeight:"600",fontSize:"11px"}}>▶ {fmtDate(entry.startDate)}</span>}
        {entry.endDate&&<><span style={{color:"#475569"}}>→</span>
          <span style={{color:"#F87171",fontWeight:"600",fontSize:"11px"}}>■ {fmtDate(entry.endDate)}</span></>}
        {dur&&<span style={{marginLeft:"auto",fontSize:"10px",color:"#64748B"}}>{dur}mo</span>}
        {!entry.startDate&&!entry.endDate&&entry.displayTime&&
          <span style={{color:"#9CA3AF",fontSize:"11px"}}>📅 {entry.displayTime}</span>}
      </div>
    </div>,
    document.body
  );
}

// ─── Creative time navigator ──────────────────
// A mini-map + draggable viewport window
function TimeNavigator({ minDate, maxDate, totalMs, viewStart, viewEnd, onViewChange, entries }) {
  const trackRef = useRef(null);
  const isDragging = useRef(false);
  const dragStart  = useRef(null);
  const dragType   = useRef(null); // "window" | "left" | "right"

  const viewPct = {
    left:  ((viewStart - minDate) / totalMs) * 100,
    right: ((viewEnd   - minDate) / totalMs) * 100,
  };
  const winWidth = viewPct.right - viewPct.left;

  // Mini-map bars
  const miniRows = entries.slice(0,40).map(e => {
    if (!e.startDate) return null;
    const left = Math.max(0,((e.startDate-minDate)/totalMs)*100);
    const right= Math.min(100, e.endDate ? ((e.endDate-minDate)/totalMs)*100 : left+1);
    const ph   = PHASE_CFG[e.phase]||PHASE_CFG["Other"];
    return { left, width:right-left, color:ph.bar };
  }).filter(Boolean);

  // Today
  const now = new Date();
  const todayPct = Math.max(0,Math.min(100,((now-minDate)/totalMs)*100));

  const pctToDate = pct => new Date(minDate.getTime() + (pct/100)*totalMs);

  const getTrackPct = clientX => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    return Math.max(0,Math.min(100,((clientX-rect.left)/rect.width)*100));
  };

  const onMouseDown = (e, type) => {
    e.preventDefault();
    isDragging.current = true;
    dragType.current   = type;
    dragStart.current  = { x:e.clientX, left:viewPct.left, right:viewPct.right };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup",   onMouseUp);
  };

  const onMouseMove = e => {
    if (!isDragging.current||!trackRef.current) return;
    const rect  = trackRef.current.getBoundingClientRect();
    const delta = ((e.clientX - dragStart.current.x) / rect.width) * 100;
    let { left, right } = dragStart.current;
    const minWin = 5;

    if (dragType.current==="window") {
      const span = right-left;
      let nl = Math.max(0, left+delta);
      let nr = nl+span;
      if (nr>100) { nr=100; nl=nr-span; }
      onViewChange(pctToDate(nl), pctToDate(nr));
    } else if (dragType.current==="left") {
      const nl = Math.max(0, Math.min(right-minWin, left+delta));
      onViewChange(pctToDate(nl), pctToDate(right));
    } else {
      const nr = Math.min(100, Math.max(left+minWin, right+delta));
      onViewChange(pctToDate(left), pctToDate(nr));
    }
  };

  const onMouseUp = () => {
    isDragging.current = false;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup",   onMouseUp);
  };

  // Click on track to jump
  const onTrackClick = e => {
    if (isDragging.current) return;
    const pct  = getTrackPct(e.clientX);
    const span = viewPct.right - viewPct.left;
    let nl = Math.max(0, pct - span/2);
    let nr = nl + span;
    if (nr>100) { nr=100; nl=nr-span; }
    onViewChange(pctToDate(nl), pctToDate(nr));
  };

  return (
    <div style={{padding:"8px 0 4px"}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:"4px"}}>
        <span style={{fontSize:"10px",color:"#9CA3AF",fontWeight:"600"}}>
          {fmtDate(minDate)}
        </span>
        <span style={{fontSize:"10px",color:"#6366F1",fontWeight:"700"}}>
          {fmtDate(viewStart)} → {fmtDate(viewEnd)}
        </span>
        <span style={{fontSize:"10px",color:"#9CA3AF",fontWeight:"600"}}>
          {fmtDate(maxDate)}
        </span>
      </div>
      {/* Track */}
      <div ref={trackRef} onClick={onTrackClick}
        style={{position:"relative",height:"36px",borderRadius:"8px",
          background:"#F1F5F9",border:"1px solid #E2E8F0",cursor:"pointer",overflow:"hidden"}}>

        {/* Mini bars */}
        {miniRows.map((r,i) => (
          <div key={i} style={{position:"absolute",left:`${r.left}%`,width:`${Math.max(r.width,0.5)}%`,
            top:"4px",height:"8px",background:r.color,opacity:0.5,borderRadius:"2px"}}/>
        ))}

        {/* Today tick */}
        <div style={{position:"absolute",left:`${todayPct}%`,top:0,bottom:0,
          width:"2px",background:"#6366F1",opacity:0.7,zIndex:3}}/>

        {/* Shaded outside viewport */}
        <div style={{position:"absolute",left:0,width:`${viewPct.left}%`,top:0,bottom:0,
          background:"rgba(0,0,0,0.15)",zIndex:2}}/>
        <div style={{position:"absolute",left:`${viewPct.right}%`,right:0,top:0,bottom:0,
          background:"rgba(0,0,0,0.15)",zIndex:2}}/>

        {/* Viewport window */}
        <div onMouseDown={e=>onMouseDown(e,"window")}
          style={{position:"absolute",left:`${viewPct.left}%`,width:`${winWidth}%`,top:0,bottom:0,
            border:"2px solid #6366F1",borderRadius:"6px",cursor:"grab",zIndex:4,
            background:"rgba(99,102,241,0.08)",boxSizing:"border-box"}}>
          {/* Left handle */}
          <div onMouseDown={e=>{e.stopPropagation();onMouseDown(e,"left");}}
            style={{position:"absolute",left:"-1px",top:"50%",transform:"translateY(-50%)",
              width:"8px",height:"20px",background:"#6366F1",borderRadius:"3px 0 0 3px",
              cursor:"ew-resize",zIndex:5,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{width:"2px",height:"10px",background:"rgba(255,255,255,0.6)",borderRadius:"1px"}}/>
          </div>
          {/* Right handle */}
          <div onMouseDown={e=>{e.stopPropagation();onMouseDown(e,"right");}}
            style={{position:"absolute",right:"-1px",top:"50%",transform:"translateY(-50%)",
              width:"8px",height:"20px",background:"#6366F1",borderRadius:"0 3px 3px 0",
              cursor:"ew-resize",zIndex:5,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{width:"2px",height:"10px",background:"rgba(255,255,255,0.6)",borderRadius:"1px"}}/>
          </div>
        </div>
      </div>

      {/* Quick jump buttons */}
      <div style={{display:"flex",gap:"6px",marginTop:"6px",justifyContent:"center"}}>
        {[
          {label:"Past 2yr", fn:()=>{const e=new Date();const s=new Date(e);s.setFullYear(s.getFullYear()-2);onViewChange(s,e);}},
          {label:"Past 1yr", fn:()=>{const e=new Date();const s=new Date(e);s.setFullYear(s.getFullYear()-1);onViewChange(s,e);}},
          {label:"Now ±1yr",fn:()=>{const n=new Date();const s=new Date(n);s.setFullYear(s.getFullYear()-1);const e=new Date(n);e.setFullYear(e.getFullYear()+1);onViewChange(s,e);}},
          {label:"Next 2yr", fn:()=>{const s=new Date();const e=new Date(s);e.setFullYear(e.getFullYear()+2);onViewChange(s,e);}},
          {label:"All",       fn:()=>onViewChange(minDate,maxDate)},
        ].map(b=>(
          <button key={b.label} onClick={b.fn}
            style={{padding:"2px 10px",borderRadius:"12px",border:"1px solid #E5E7EB",
              background:"#fff",color:"#6B7280",fontSize:"10px",fontWeight:"600",cursor:"pointer",
              transition:"all 0.15s"}}>
            {b.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Quarter/Month header ─────────────────────
function TimelineHeader({ viewStart, viewEnd, viewMs }) {
  const months = [];
  const cursor = new Date(viewStart.getFullYear(), viewStart.getMonth(), 1);
  const MON_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  while (cursor <= viewEnd) {
    const left  = ((cursor-viewStart)/viewMs)*100;
    const q     = Math.floor(cursor.getMonth()/3)+1;
    const isQS  = cursor.getMonth()%3===0;
    const now   = new Date();
    const isNow = cursor.getFullYear()===now.getFullYear()&&
                  Math.floor(cursor.getMonth()/3)===Math.floor(now.getMonth()/3);
    months.push({left, label:MON_NAMES[cursor.getMonth()], year:cursor.getFullYear(), q, isQS, isNow});
    cursor.setMonth(cursor.getMonth()+1);
  }

  // Q labels at midpoint of each quarter
  const qLabels = [];
  let i=0;
  while(i<months.length){
    if(months[i].isQS||i===0){
      const slice=months.slice(i,i+3);
      const mid=slice[Math.floor(slice.length/2)];
      qLabels.push({left:mid.left,label:`Q${months[i].q} ${months[i].year}`,isNow:months[i].isNow});
      i+=3;
    } else i++;
  }

  const showMonths = months.length <= 36;

  return (
    <div style={{position:"relative",height:"40px",borderBottom:"2px solid #E2E8F0",
      background:"#F8FAFC",overflow:"hidden"}}>
      {/* Quarter labels */}
      {qLabels.map((q,i)=>(
        <div key={i} style={{position:"absolute",left:`${q.left}%`,transform:"translateX(-50%)",
          top:"2px",fontSize:"10px",fontWeight:"800",whiteSpace:"nowrap",
          color:q.isNow?"#6366F1":"#374151",
          background:q.isNow?"#EEF2FF":"transparent",
          padding:"1px 6px",borderRadius:"4px"}}>
          {q.label}
        </div>
      ))}
      {/* Month ticks + labels */}
      {months.map((m,i)=>(
        <div key={i}>
          <div style={{position:"absolute",left:`${m.left}%`,bottom:0,
            height:m.isQS?"18px":"10px",width:"1px",
            background:m.isQS?"#94A3B8":"#E2E8F0"}}/>
          {showMonths&&(
            <span style={{position:"absolute",left:`${m.left+0.3}%`,bottom:"2px",
              fontSize:"8px",color:m.isNow?"#6366F1":"#94A3B8",
              fontWeight:m.isNow?"700":"400",whiteSpace:"nowrap"}}>
              {m.label}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Today line ───────────────────────────────
function TodayLine({ viewStart, viewMs }) {
  const now=new Date();
  const left=((now-viewStart)/viewMs)*100;
  if(left<0||left>100) return null;
  return (
    <div style={{position:"absolute",left:`${left}%`,top:0,bottom:0,
      width:"2px",background:"#6366F1",opacity:0.55,pointerEvents:"none",zIndex:3}}>
      <div style={{position:"absolute",top:0,left:"-5px",width:"12px",height:"12px",
        borderRadius:"50%",background:"#6366F1"}}/>
      <div style={{position:"absolute",top:"14px",left:"4px",fontSize:"8px",
        color:"#fff",fontWeight:"800",background:"#6366F1",
        padding:"1px 5px",borderRadius:"3px",whiteSpace:"nowrap"}}>NOW</div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────
// ─── Drag-to-scroll hook ──────────────────────
function useDragScroll() {
  const ref = useRef(null);
  const drag = useRef({ active:false, startX:0, scrollX:0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onDown = e => {
      drag.current = { active:true, startX:e.clientX, scrollX:el.scrollLeft };
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
    };
    const onMove = e => {
      if (!drag.current.active) return;
      el.scrollLeft = drag.current.scrollX - (e.clientX - drag.current.startX);
    };
    const onUp = () => {
      if (!drag.current.active) return;
      drag.current.active = false;
      el.style.cursor = "grab";
      el.style.userSelect = "";
    };

    el.addEventListener("mousedown", onDown);
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseup", onUp);
    el.addEventListener("mouseleave", onUp);

    return () => {
      el.removeEventListener("mousedown", onDown);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseup", onUp);
      el.removeEventListener("mouseleave", onUp);
    };
  });  // no deps — re-attaches if ref changes

  return ref;
}

function ScrollChips({ label, items, active, onSelect, activeColor="#6366F1", activeBg="#EEF2FF", renderLabel }) {
  const ref = useDragScroll();
  if (!items.length) return null;
  return (
    <div style={{display:"flex",alignItems:"center",gap:"6px",minWidth:0}}>
      <span style={{fontSize:"10px",fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",
        letterSpacing:"0.5px",flexShrink:0,minWidth:"56px"}}>{label}</span>
      <div ref={ref} style={{flex:1,minWidth:0,overflowX:"auto",overflowY:"hidden",
        scrollbarWidth:"none",msOverflowStyle:"none",cursor:"grab",
        WebkitOverflowScrolling:"touch",userSelect:"none"}}>
        <div style={{display:"flex",gap:"6px",width:"max-content",padding:"2px 0"}}>
          {items.map(item=>{
            const a=active===item;
            return (
              <button key={item}
                onMouseDown={e=>e.stopPropagation()}
                onClick={()=>onSelect(a?null:item)}
                style={{padding:"3px 10px",borderRadius:"20px",whiteSpace:"nowrap",flexShrink:0,
                  border:`1.5px solid ${a?activeColor:"#E5E7EB"}`,
                  background:a?activeBg:"#fff",color:a?activeColor:"#6B7280",
                  fontSize:"11px",fontWeight:a?"700":"500",
                  cursor:"pointer",transition:"all 0.15s"}}>
                {renderLabel ? renderLabel(item) : item}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function ClinicalTimeline({ user, subproject }) {
  const { docs, loading, error } = useClinicalLoader({ user, subproject });
  const [filterPhase,   setFilterPhase]   = useState(null);
  const [filterSponsor, setFilterSponsor] = useState(null);
  const [search,        setSearch]        = useState("");
  const [tip,           setTip]           = useState(null);
  const [tipPos,        setTipPos]        = useState({x:0,y:0});
  const ctx = useContext(ThemeCtx)||{};
  const theme = ctx.theme || ctx || {};

  const allEntries = buildEntries(docs);

  const entries = allEntries.filter(e => {
    if (filterPhase   && e.phase   !== filterPhase)  return false;
    if (filterSponsor && !(e.allSponsors||[e.sponsor]).includes(filterSponsor)) return false;
    if (search) {
      const q=search.toLowerCase();
      if(![e.subject,e.acronym,e.product,e.sponsor,e.indication].some(v=>v?.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const sorted = [...entries].sort((a,b)=>{
    const pi=PHASE_ORDER.indexOf(a.phase)-PHASE_ORDER.indexOf(b.phase);
    if(pi!==0) return pi;
    return (a.startDate||new Date(9999,0))-(b.startDate||new Date(9999,0));
  });

  const allDates = allEntries.flatMap(e=>[e.startDate,e.endDate].filter(Boolean));
  const now = new Date();
  const dataMin = allDates.length ? new Date(Math.min(...allDates)) : new Date(now.getFullYear()-1,0,1);
  const dataMax = allDates.length ? new Date(Math.max(...allDates)) : new Date(now.getFullYear()+2,0,1);
  // Pad 3 months each side
  const minDate = new Date(dataMin); minDate.setMonth(minDate.getMonth()-3);
  const maxDate = new Date(dataMax); maxDate.setMonth(maxDate.getMonth()+3);
  const totalMs = maxDate - minDate;

  // Viewport: default to now ±18 months
  const defStart = new Date(now); defStart.setMonth(defStart.getMonth()-18);
  const defEnd   = new Date(now); defEnd.setMonth(defEnd.getMonth()+18);
  const [viewStart, setViewStart] = useState(() => new Date(Math.max(minDate,defStart)));
  const [viewEnd,   setViewEnd]   = useState(() => new Date(Math.min(maxDate,defEnd)));

  const handleViewChange = (s, e) => {
    setViewStart(new Date(Math.max(minDate, s)));
    setViewEnd(new Date(Math.min(maxDate, e)));
  };

  const viewMs = viewEnd - viewStart;

  const phases   = PHASE_ORDER.filter(ph=>sorted.some(e=>e.phase===ph));
  const sponsors = [...new Set(allEntries.flatMap(e=>e.allSponsors||[e.sponsor]).filter(Boolean))].sort();

  const LEFT_W = 240; // px reference — actual layout uses clamp via style

  const Spinner = () => (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",padding:"4rem",color:"#9CA3AF"}}>
      <div style={{width:"16px",height:"16px",border:"2px solid #E5E7EB",
        borderTopColor:theme.primary||C.primary,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
      Loading trials...
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );

  return (
    <div style={{padding:"20px",display:"flex",flexDirection:"column",height:"100%",boxSizing:"border-box",gap:"10px"}}>

      {/* ── Header ── */}
      <div style={{display:"flex",alignItems:"center",gap:"12px",flexWrap:"wrap"}}>
        <div>
          <div style={{fontSize:"19px",fontWeight:"700",color:"#111827"}}>Clinical Trial Timeline</div>
          <div style={{fontSize:"13px",color:"#9CA3AF",marginTop:"1px"}}>
            {subproject?.SUMMARY} · {sorted.length}{sorted.length!==allEntries.length?` of ${allEntries.length}`:""} trials
          </div>
        </div>
        {/* Phase legend pills — clickable */}
        <div style={{display:"flex",gap:"7px",flexWrap:"wrap",marginLeft:"auto",alignItems:"center"}}>
          {PHASE_ORDER.filter(ph=>allEntries.some(e=>e.phase===ph)).map(ph=>{
            const cfg=PHASE_CFG[ph], active=filterPhase===ph;
            return (
              <button key={ph} onClick={()=>setFilterPhase(active?null:ph)}
                style={{display:"flex",alignItems:"center",gap:"5px",padding:"4px 11px",
                  borderRadius:"20px",border:`1.5px solid ${active?cfg.bar:"#E5E7EB"}`,
                  background:active?cfg.light:"#fff",cursor:"pointer",transition:"all 0.15s"}}>
                <div style={{width:"8px",height:"8px",borderRadius:"50%",background:cfg.bar}}/>
                <span style={{fontSize:"11px",fontWeight:active?"700":"500",color:active?cfg.text:"#6B7280"}}>{ph}</span>
              </button>
            );
          })}
          <div style={{width:"1px",height:"18px",background:"#E5E7EB"}}/>
          <div style={{display:"flex",alignItems:"center",gap:"4px"}}>
            <div style={{width:"2px",height:"16px",background:"#6366F1",borderRadius:"1px"}}/>
            <span style={{fontSize:"11px",color:"#6B7280"}}>Now</span>
          </div>
        </div>
      </div>

      {error&&<div style={{padding:"10px 14px",borderRadius:"8px",background:"#FEF2F2",color:"#DC2626",fontSize:"13px"}}>{error}</div>}

      {/* ── Search + scrollable sponsor chips ── */}
      <div style={{display:"flex",flexDirection:"column",gap:"6px",
        background:"#F8FAFC",border:"1px solid #E5E7EB",borderRadius:"12px",padding:"10px 12px"}}>
        <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
            {(filterPhase||filterSponsor)&&(
            <button onClick={()=>{setFilterPhase(null);setFilterSponsor(null);setSearch("");}}
              style={{padding:"4px 12px",borderRadius:"20px",border:"1px solid #FECACA",
                background:"#FEF2F2",color:"#EF4444",fontSize:"11px",fontWeight:"600",cursor:"pointer",flexShrink:0}}>
              ✕ Clear all
            </button>
          )}
          {filterSponsor&&<span style={{fontSize:"11px",color:"#6366F1",fontWeight:"600"}}>{filterSponsor}</span>}
        </div>
        <ScrollChips label="Sponsor" items={sponsors} active={filterSponsor} onSelect={setFilterSponsor}/>
      </div>

      {loading&&<Spinner/>}

      {!loading&&sorted.length===0&&(
        <div style={{textAlign:"center",padding:"4rem"}}>
          <div style={{fontSize:"40px",opacity:0.15,marginBottom:"1rem"}}>🧪</div>
          <div style={{fontSize:"15px",color:"#9CA3AF"}}>No trials match your filters</div>
        </div>
      )}

      {!loading&&sorted.length>0&&<>
        {/* ── Gantt chart ── */}
        <div style={{flex:1,display:"flex",flexDirection:"column",background:"#fff",
          borderRadius:"14px",border:"1px solid #E5E7EB",overflow:"hidden",
          boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>

          {/* Sticky header row */}
          <div style={{display:"flex",borderBottom:"2px solid #E2E8F0",background:"#F8FAFC",flexShrink:0}}>
            <div style={{width:"clamp(160px, 18vw, 240px)",flexShrink:0,padding:"10px 14px",
              fontSize:"10px",fontWeight:"700",color:"#9CA3AF",
              textTransform:"uppercase",letterSpacing:"0.6px",borderRight:"1px solid #E2E8F0"}}>
              Product / Sponsor
            </div>
            <div style={{flex:1}}>
              <TimelineHeader viewStart={viewStart} viewEnd={viewEnd} viewMs={viewMs}/>
            </div>
            <div style={{width:"clamp(90px, 9vw, 120px)",flexShrink:0,padding:"10px",
              fontSize:"10px",fontWeight:"700",color:"#9CA3AF",
              textTransform:"uppercase",letterSpacing:"0.6px",
              borderLeft:"1px solid #E2E8F0",textAlign:"right"}}>
              Dates
            </div>
          </div>

          {/* Scrollable body */}
          <div style={{flex:1,overflowY:"auto"}}>
            {phases.map(ph=>{
              const phEntries=sorted.filter(e=>e.phase===ph);
              if(!phEntries.length) return null;
              const cfg=PHASE_CFG[ph];
              return (
                <div key={ph}>
                  {/* Phase group header */}
                  <div style={{display:"flex",alignItems:"center",
                    background:cfg.grad,padding:"5px 14px",gap:"10px",position:"sticky",top:0,zIndex:2,minWidth:0}}>
                    <span style={{fontSize:"11px",fontWeight:"800",color:"#fff",
                      textTransform:"uppercase",letterSpacing:"0.8px"}}>{ph}</span>
                    <span style={{fontSize:"11px",fontWeight:"700",color:"rgba(255,255,255,0.8)",
                      background:"rgba(255,255,255,0.2)",borderRadius:"10px",padding:"0 8px"}}>{phEntries.length}</span>
                  </div>

                  {/* Rows */}
                  {phEntries.map((entry,ri)=>{
                    const pc=PHASE_CFG[entry.phase]||PHASE_CFG["Other"];
                    let bar=null;
                    if(entry.startDate&&entry.endDate&&entry.endDate>entry.startDate){
                      const l=Math.max(0,((entry.startDate-viewStart)/viewMs)*100);
                      const r=Math.min(100,((entry.endDate-viewStart)/viewMs)*100);
                      if(r>0&&l<100) bar={left:`${l}%`,width:`${r-l}%`};
                    } else if(entry.startDate){
                      const l=((entry.startDate-viewStart)/viewMs)*100;
                      if(l>=0&&l<=100) bar={left:`${l}%`,width:"1.2%"};
                    }

                    return (
                      <div key={entry.id}
                        style={{display:"flex",alignItems:"stretch",
                          borderBottom:"1px solid #F1F5F9",minHeight:"46px",
                          background:ri%2===0?"#fff":"#FAFBFF",
                          transition:"background 0.1s",cursor:"pointer"}}
                        onClick={()=>openDocTab({DOCUMENTID:entry.id,DOCTYPEID:96},subproject?.SUBPROJECTID)}
                        onMouseEnter={e=>{setTip(entry);setTipPos({x:e.clientX,y:e.clientY});}}
                        onMouseLeave={()=>setTip(null)}>

                        {/* Left: product+sponsor */}
                        <div style={{width:"clamp(160px, 18vw, 240px)",flexShrink:0,padding:"7px 12px",
                          display:"flex",alignItems:"center",gap:"8px",
                          borderRight:"1px solid #F1F5F9"}}>
                          <div style={{width:"4px",height:"28px",borderRadius:"2px",
                            background:pc.grad,flexShrink:0}}/>
                          {entry.logoUrl
                            ? <img src={entry.logoUrl} alt="" onError={e=>e.target.style.display="none"}
                                style={{width:"22px",height:"22px",objectFit:"contain",borderRadius:"4px",flexShrink:0}}/>
                            : <div style={{width:"22px",height:"22px",borderRadius:"5px",background:pc.light,
                                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                <span style={{fontSize:"9px",fontWeight:"800",color:pc.bar}}>{entry.sponsor?.[0]||"?"}</span>
                              </div>}
                          <div style={{minWidth:0}}>
                            <div style={{fontSize:"11px",fontWeight:"700",color:"#1E293B",
                              overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {entry.product||entry.acronym||entry.subject.slice(0,22)}
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:"4px",marginTop:"1px",flexWrap:"wrap"}}>
                            {entry.sponsor&&<span style={{fontSize:"9px",color:"#94A3B8",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{entry.sponsor}</span>}
                            {entry.countries&&entry.countries.slice(0,3).map((c,i)=>(
                              <FlagIcon key={i} country={c} size={12}/>
                            ))}
                          </div>
                          </div>
                        </div>

                        {/* Gantt area */}
                        <div style={{flex:1,position:"relative",overflow:"hidden"}}>
                          <TodayLine viewStart={viewStart} viewMs={viewMs}/>
                          {bar
                            ? <div style={{position:"absolute",top:"9px",height:"28px",zIndex:1,...bar,
                                background:pc.grad,borderRadius:"6px",
                                boxShadow:`0 2px 8px ${pc.bar}33`,
                                display:"flex",alignItems:"center",overflow:"hidden",
                                paddingLeft:"8px",minWidth:"4px"}}>
                                <span style={{fontSize:"9px",color:"rgba(255,255,255,0.95)",
                                  fontWeight:"700",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                                  {entry.acronym||entry.product||""}
                                </span>
                                {entry.startDate&&entry.endDate&&(
                                  <span style={{marginLeft:"auto",paddingRight:"6px",fontSize:"8px",
                                    color:"rgba(255,255,255,0.7)",whiteSpace:"nowrap",flexShrink:0}}>
                                    {Math.round((entry.endDate-entry.startDate)/(1000*60*60*24*30))}mo
                                  </span>
                                )}
                              </div>
                            : <div style={{position:"absolute",top:"50%",left:"8px",
                                transform:"translateY(-50%)",width:"10px",height:"10px",
                                borderRadius:"50%",background:pc.bar,zIndex:1,
                                boxShadow:`0 0 0 3px ${pc.light}`}}/>
                          }
                        </div>

                        {/* Right: dates */}
                        <div style={{width:"clamp(90px, 9vw, 120px)",flexShrink:0,padding:"7px 10px",
                          borderLeft:"1px solid #F1F5F9",display:"flex",
                          flexDirection:"column",justifyContent:"center"}}>
                          {entry.startDate&&<div style={{fontSize:"10px",color:"#059669",fontWeight:"600"}}>▶ {fmtDate(entry.startDate)}</div>}
                          {entry.endDate  &&<div style={{fontSize:"10px",color:"#EF4444",fontWeight:"600"}}>■ {fmtDate(entry.endDate)}</div>}
                          {!entry.startDate&&!entry.endDate&&<div style={{fontSize:"10px",color:"#9CA3AF"}}>{entry.displayTime}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Time navigator ── */}
        <div style={{background:"#fff",borderRadius:"12px",border:"1px solid #E5E7EB",
          padding:"10px 14px",boxShadow:"0 1px 6px rgba(0,0,0,0.05)"}}>
          <TimeNavigator
            minDate={minDate} maxDate={maxDate} totalMs={totalMs}
            viewStart={viewStart} viewEnd={viewEnd}
            onViewChange={handleViewChange}
            entries={sorted}/>
        </div>
      </>}

      {tip&&<Tooltip entry={tip} x={tipPos.x} y={tipPos.y}/>}
    </div>
  );
}